import {
  findProfileDetails,
  readProfileOrganization,
  readProfileRole,
  readProfileText,
  type SessionProfile,
} from './session-profile'

export const sessionProfileStorageKeys = {
  profile: 'forgesphere.session.profile',
  status: 'forgesphere.session.status',
  checkedAt: 'forgesphere.session.checkedAt',
} as const

export type SessionStatus = 'active' | 'expired'

export interface StoredSessionIdentity {
  id: string
  name: string
  email: string
}

export function saveSessionProfile(profile: SessionProfile): void {
  if (typeof window === 'undefined') return

  const details = findProfileDetails(profile)
  const firstName = readProfileText(details, [
    'firstName',
    'first_name',
    'givenName',
    'given_name',
  ])
  const lastName = readProfileText(details, [
    'lastName',
    'last_name',
    'familyName',
    'family_name',
  ])
  const displayName =
    readProfileText(details, [
      'displayName',
      'display_name',
      'fullName',
      'full_name',
      'name',
      'username',
      'preferredUsername',
      'preferred_username',
    ]) || [firstName, lastName].filter(Boolean).join(' ')
  const organization = readProfileOrganization(details)
  const organizationId = readProfileText(details, [
    'organizationId',
    'organization_id',
    'companyId',
    'company_id',
    'tenantId',
    'tenant_id',
  ])
  const userId = readProfileText(details, [
    'id',
    'userId',
    'user_id',
    'sub',
    'subject',
  ])

  writeStorage(sessionProfileStorageKeys.profile, JSON.stringify(profile))
  writeStorage(sessionProfileStorageKeys.status, 'active')
  writeStorage(sessionProfileStorageKeys.checkedAt, new Date().toISOString())
  writeOptionalStorage(
    'userEmail',
    readProfileText(details, ['email', 'userEmail', 'user_email']),
  )
  writeOptionalStorage('userId', userId)
  writeOptionalStorage('userFirstName', firstName)
  writeOptionalStorage('userName', displayName)
  writeOptionalStorage('userRole', readProfileRole(details))
  writeOptionalStorage('userOrganization', organization)
  writeOptionalStorage('userOrganizationId', organizationId)
}

export function markSessionExpired(): void {
  if (typeof window === 'undefined') return

  ;[
    sessionProfileStorageKeys.profile,
    'userId',
    'userEmail',
    'userFirstName',
    'userName',
    'userRole',
    'userOrganization',
    'userOrganizationId',
  ].forEach((key) => window.localStorage.removeItem(key))
  writeStorage(sessionProfileStorageKeys.status, 'expired')
  writeStorage(sessionProfileStorageKeys.checkedAt, new Date().toISOString())
}

export function getStoredSessionIdentity(): StoredSessionIdentity {
  if (typeof window === 'undefined') return { id: '', name: '', email: '' }

  let details: Record<string, unknown> = {}
  const storedProfile = window.localStorage.getItem(
    sessionProfileStorageKeys.profile,
  )
  if (storedProfile) {
    try {
      details = findProfileDetails(JSON.parse(storedProfile) as SessionProfile)
    } catch {
      // Legacy identity keys remain usable if the cached payload is invalid.
    }
  }

  return {
    id:
      readProfileText(details, ['id', 'userId', 'user_id', 'sub', 'subject']) ||
      window.localStorage.getItem('userId')?.trim() ||
      '',
    name:
      readProfileText(details, [
        'displayName',
        'display_name',
        'fullName',
        'full_name',
        'name',
        'username',
        'preferredUsername',
        'preferred_username',
      ]) ||
      window.localStorage.getItem('userName')?.trim() ||
      '',
    email:
      readProfileText(details, ['email', 'userEmail', 'user_email']) ||
      window.localStorage.getItem('userEmail')?.trim() ||
      '',
  }
}

export function clearSessionProfile(): void {
  if (typeof window === 'undefined') return
  Object.values(sessionProfileStorageKeys).forEach((key) =>
    window.localStorage.removeItem(key),
  )
}

function writeOptionalStorage(key: string, value: string) {
  if (value) writeStorage(key, value)
}

function writeStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Storage restrictions must not prevent the authenticated application shell.
  }
}
